import { i18n } from '../services/i18n.js';
import { dbSim } from '../services/dbSim.js';
import { realtimeSim } from '../services/realtimeSim.js';
import { escapeHTML } from '../services/sanitize.js';
import { icon } from '../services/icons.js';
import { showReaction } from '../services/reactions.js';

export const LeaderboardView = async (container) => {
  // Parse query parameters
  const hashParts = window.location.hash.split('?');
  const query = hashParts[1] || '';
  const urlParams = new URLSearchParams(query);
  const sessionId = urlParams.get('session');

  container.innerHTML = `<div class="container" style="padding:40px; text-align:center; color:var(--color-text-secondary);">${i18n.t('loading_leaderboard')}</div>`;

  const session = await dbSim.getSession(sessionId);
  if (!session) {
    window.showToast(i18n.t('session_not_found'));
    window.location.hash = '#/dashboard';
    return;
  }
  if (session.status !== 'results_revealed') {
    window.showToast(session.status === 'finished' ? i18n.t('game_finished') : i18n.t('results_not_revealed'));
    window.location.hash = session.status === 'finished' ? `#/podium?session=${session.id}` : '#/dashboard';
    return;
  }

  const game = await dbSim.getSessionGame(session.id);
  const qIdx = session.currentQuestionIndex;
  const isLastQuestion = qIdx === game.questions.length - 1;

  // 1. Calculate Ranks and Rank Changes
  const participants = await dbSim.getParticipants(session.id);
  const responses = await dbSim.getResponses(session.id);
  const currentQuestionId = game.questions[qIdx].id;

  // Calculate previous ranks
  const participantsWithPrevScores = participants.map(p => {
    // Sum points for all questions except the current one
    const prevResponses = responses.filter(r => r.participantId === p.id && r.questionId !== currentQuestionId);
    const prevScore = prevResponses.reduce((sum, r) => sum + r.pointsAwarded, 0);
    return { ...p, prevScore };
  });

  // Sort by previous score to assign previous ranks
  const sortedPrev = [...participantsWithPrevScores].sort((a, b) => b.prevScore - a.prevScore);
  const prevRankMap = {};
  sortedPrev.forEach((p, idx) => {
    prevRankMap[p.id] = idx + 1; // 1-indexed
  });

  // Calculate current ranks
  const leaderboard = await dbSim.getLeaderboard(session.id);
  const top10 = leaderboard.slice(0, 10).map((p, idx) => {
    const currentRank = idx + 1;
    const prevRank = prevRankMap[p.id] || currentRank;
    const rankChange = prevRank - currentRank; // positive means went up
    return { ...p, currentRank, rankChange };
  });

  const getLongestStreakPlayer = (parts) => {
    if (parts.length === 0) return i18n.t('no_result');
    const sorted = [...parts].sort((a, b) => b.streak - a.streak);
    return sorted[0].streak > 0 ? `${sorted[0].nickname} (${sorted[0].streak} ${i18n.t('questions')})` : i18n.t('no_result');
  };

  const getFastestAnswerPlayer = (parts, resps, qId) => {
    const respsFiltered = resps.filter(r => r.questionId === qId && r.isCorrect);
    if (respsFiltered.length === 0) return i18n.t('no_result');
    const sorted = respsFiltered.sort((a, b) => a.responseTimeMs - b.responseTimeMs);
    const part = parts.find(p => p.id === sorted[0].participantId);
    return part ? `${part.nickname} (${(sorted[0].responseTimeMs / 1000).toFixed(2)}s)` : i18n.t('no_result');
  };

  const triggerNextStep = async () => {
    if (isLastQuestion) {
      // Transition session to finished
      session.status = "finished";
      await dbSim.saveSession(session);

      // Publish game over
      realtimeSim.publish("GAME_OVER", { sessionId: session.id });

      // Redirect to podium
      window.location.hash = `#/podium?session=${session.id}`;
    } else {
      // Increment question index
      session.currentQuestionIndex += 1;
      session.status = "active";
      session.currentQuestionStartedAt = new Date().toISOString();
      await dbSim.saveSession(session);

      // Publish next question event
      realtimeSim.publish("NEXT_QUESTION", {
        sessionId: session.id,
        currentQuestionIndex: session.currentQuestionIndex,
        currentQuestionStartedAt: session.currentQuestionStartedAt
      });

      // Redirect back to present screen
      window.location.hash = `#/sessions/${session.id}/present`;
    }
  };

  const render = () => {
    container.innerHTML = `
      <div class="host-screen" style="flex:1; display:flex; flex-direction:column; background: var(--color-bg); padding:40px; justify-content:space-between; min-height:100vh;">
        
        <!-- Header -->
        <div class="host-header" style="display:flex; justify-content:space-between; align-items:center;">
          <span class="badge badge-red" style="font-size:14px; padding:6px 14px;">
            ${i18n.t('score_after_question')} ${qIdx + 1}/${game.questions.length}
          </span>
          <div class="session-brand">
            ${game.brand?.name ? `<strong>${escapeHTML(game.brand.name)}</strong>` : ''}
            <span>${i18n.t('leaderboard_title')}</span>
          </div>
          <button id="nextActionBtn" class="btn btn-primary btn-lg" style="box-shadow: var(--shadow-glow); min-width:160px;">
            ${isLastQuestion ? `${icon('trophy')} ${i18n.t('view_podium')}` : `${i18n.t('next_question')} ${icon('arrowRight')}`}
          </button>
        </div>
 
        <!-- Leaderboard Rows container -->
        <div style="max-width:800px; width:100%; margin: 40px auto; flex:1;">
          <div class="leaderboard-list">
            ${top10.map((p, idx) => {
              const changeClass = p.rankChange > 0 ? 'change-up' : (p.rankChange < 0 ? 'change-down' : 'change-none');
              const changeText = p.rankChange > 0 ? `↑ +${p.rankChange}` : (p.rankChange < 0 ? `↓ ${p.rankChange}` : '•');
              
              // Top 3 medals display
              const medalColors = ['#facc15', '#cbd5e1', '#d97706'];
              const rankDisplay = p.currentRank <= 3
                ? `<span style="color:${medalColors[p.currentRank - 1]};">${icon('trophy')}</span>`
                : p.currentRank;
 
              return `
                <div class="leaderboard-row" style="transform: translateY(20px); opacity: 0; animation: slideUpRow 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${idx * 0.1}s forwards;">
                  <div class="row-left">
                    <span class="row-rank row-rank-${p.currentRank}">${rankDisplay}</span>
                    <span class="row-nickname">${escapeHTML(p.nickname)}</span>
                  </div>
                  <div class="row-right">
                    <span class="row-score">${p.score} ${i18n.t('play_points')}</span>
                    <span class="row-change ${changeClass}">${changeText}</span>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
 
        <!-- Footnote: Fastest Answer / Longest Streak -->
        <div class="leaderboard-insights" style="display:flex; justify-content:center; gap:40px; margin-top:20px; font-weight:700;">
          <div style="color:var(--color-accent);">${icon('fire')} Longest Streak: ${escapeHTML(getLongestStreakPlayer(participants))}</div>
          <div style="color:var(--color-success);">${icon('bolt')} Fastest Answer: ${escapeHTML(getFastestAnswerPlayer(participants, responses, currentQuestionId))}</div>
        </div>
 
      </div>
    `;

    // Inject css for row delay transitions
    if (!document.getElementById('leaderboard-styles')) {
      const style = document.createElement('style');
      style.id = 'leaderboard-styles';
      style.innerHTML = `
        @keyframes slideUpRow {
          0% { transform: translateY(30px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
 
    // Bind next trigger action
    container.querySelector('#nextActionBtn').addEventListener('click', async () => {
      await triggerNextStep();
    });
  };

  render();

  realtimeSim.connect(session.pin, (type, payload) => {
    if (type === 'SEND_REACTION') showReaction(container, payload.reaction);
  });

  window.addEventListener('hashchange', function cleanup() {
    realtimeSim.disconnect();
    window.removeEventListener('hashchange', cleanup);
  });
};
